import type { AutoConnection, RelationshipPair } from '../../entities/graph-document';

export type RelationshipEdgeKind = 'explicit-only' | 'mixed';
export type RelationshipVisibilityMode = 'overview-hidden' | 'focus-visible' | 'expand-visible';

export interface RelationshipDisplaySummary {
  pairId: string;
  sourceNodeId: string;
  targetNodeId: string;
  edgeKind: RelationshipEdgeKind;
  summaryText: string;
  fullText: string;
  derivedReasonText: string | null;
  fullDerivedReasonText: string | null;
  hasOverflow: boolean;
  visibilityMode: RelationshipVisibilityMode;
}

export function selectRelationshipSummary({
  pair,
  sourceNodeId,
  targetNodeId,
  derivedConnections = [],
  visibilityMode = 'overview-hidden',
  maxLabelsPerDirection = 2,
  maxDerivedReasons = 2,
}: {
  pair: RelationshipPair;
  sourceNodeId: string;
  targetNodeId: string;
  derivedConnections?: AutoConnection[];
  visibilityMode?: RelationshipVisibilityMode;
  maxLabelsPerDirection?: number;
  maxDerivedReasons?: number;
}): RelationshipDisplaySummary {
  const outboundLabels = directionLabels(pair, sourceNodeId, targetNodeId);
  const inboundLabels = directionLabels(pair, targetNodeId, sourceNodeId);
  const compactOutbound = compactLabels(outboundLabels, maxLabelsPerDirection);
  const compactInbound = compactLabels(inboundLabels, maxLabelsPerDirection);
  const derivedReasons = derivedConnections.map((connection) => connection.reasonSummary).filter((reason) => reason.trim().length > 0);
  const compactDerivedReasons = compactLabels(derivedReasons, maxDerivedReasons);

  return {
    pairId: pair.pairId,
    sourceNodeId,
    targetNodeId,
    edgeKind: derivedConnections.length > 0 ? 'mixed' : 'explicit-only',
    summaryText: `${compactOutbound.text} / ${compactInbound.text}`,
    fullText: `${fullLabels(outboundLabels)} / ${fullLabels(inboundLabels)}`,
    derivedReasonText: derivedReasons.length > 0 ? compactDerivedReasons.text : null,
    fullDerivedReasonText: derivedReasons.length > 0 ? fullLabels(derivedReasons) : null,
    hasOverflow: compactOutbound.overflow || compactInbound.overflow || compactDerivedReasons.overflow,
    visibilityMode,
  };
}

function compactLabels(labels: string[], maxLabelsPerDirection: number): { text: string; overflow: boolean } {
  if (labels.length === 0) {
    return { text: '—', overflow: false };
  }

  if (labels.length <= maxLabelsPerDirection) {
    return { text: labels.join('、'), overflow: false };
  }

  return {
    text: `${labels.slice(0, maxLabelsPerDirection).join('、')}…`,
    overflow: true,
  };
}

function fullLabels(labels: string[]): string {
  return labels.length > 0 ? labels.join('、') : '—';
}

function directionLabels(pair: RelationshipPair, sourceNodeId: string, targetNodeId: string): string[] {
  return pair.directions.find(
    (direction) => direction.sourceNodeId === sourceNodeId && direction.targetNodeId === targetNodeId,
  )?.labels ?? [];
}
